/**
 * ExplorerReport — the reusable GA-style report (FEAT-ANALYTICS-001).
 *
 * One component renders User/Traffic acquisition, Pages & screens, Events,
 * Demographics, Tech, etc. — driven by `dimension` + `columns`. Layout mirrors
 * GA: a multi-series "over time" chart on top (plot-rows toggles), then a rich
 * metric table (primary dimension + metric columns, % of total, a Total row,
 * sortable headers, a dimension switcher, search and pagination).
 *
 * Data: /analytics/explore (RRS → Event). xNotify brand theme throughout.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Flex, Text, HStack, Select, Input, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Checkbox, Icon, Tooltip, Button,
} from '@chakra-ui/react';
import { FiArrowDown, FiSearch, FiDownload } from 'react-icons/fi';
import { downloadCsv } from './exportCsv';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useReport } from './api';
import { Panel, ReportFrame, EmptyState, fmtInt, fmtPct, fmtDuration, brandColor } from './components';
import { countryName } from './geo';
import { fmtMoney } from './txnFormat';

const PALETTE = ['#2563EB', '#16A34A', '#EA580C', '#DB2777', '#7C3AED', '#0891B2', '#CA8A04', '#DC2626'];
const num2 = (v) => Number(v || 0).toFixed(2);
// No hardcoded currency symbol: the Explorer groups by a non-currency dimension, so
// its revenue column can span multiple currencies — show the amount without a false
// '$' (DA-BUG-0455). Per-currency totals live on the Revenue reports.
const money = (v) => fmtMoney(Number(v || 0));

// Metric column catalogue — label + formatter + whether to show a % of total.
export const METRIC_COLS = {
  users: { label: 'Active users', fmt: fmtInt, share: true },
  new_users: { label: 'New users', fmt: fmtInt, share: true },
  returning_users: { label: 'Returning users', fmt: fmtInt },
  sessions: { label: 'Sessions', fmt: fmtInt, share: true },
  engaged_sessions: { label: 'Engaged sessions', fmt: fmtInt },
  engagement_rate: { label: 'Engagement rate', fmt: (v) => `${num2(v)}%` },
  views: { label: 'Views', fmt: fmtInt, share: true },
  views_per_user: { label: 'Views / user', fmt: num2 },
  avg_engagement_seconds: { label: 'Avg engagement', fmt: fmtDuration },
  event_count: { label: 'Event count', fmt: fmtInt, share: true },
  events_per_session: { label: 'Events / session', fmt: num2 },
  events_per_user: { label: 'Events / user', fmt: num2, derive: (r) => (r.users > 0 ? r.event_count / r.users : 0) },
  key_events: { label: 'Key events', fmt: fmtInt },
  key_event_rate: { label: 'Key event rate', fmt: (v) => `${num2(v)}%` },
  conversion_rate: { label: 'Conv. rate', fmt: (v) => `${num2(v)}%` },
  revenue: { label: 'Revenue', fmt: money, share: true },
};

const fmtBucket = (b) => {
  const d = new Date(String(b).replace(' ', 'T'));
  if (isNaN(d)) return String(b).slice(5, 10);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

function MultiSeriesChart({ series, plotted, sortLabel, keyFormat = (k) => k }) {
  const lines = (series.lines || []).filter((l) => !plotted || plotted.has(l.key));
  const categories = (series.buckets || []).map(fmtBucket);
  const options = {
    credits: { enabled: false },
    title: { text: undefined },
    chart: { type: 'spline', height: 300, backgroundColor: 'transparent', style: { fontFamily: 'inherit' } },
    xAxis: { categories, tickLength: 0, lineColor: '#e5e7eb', labels: { style: { color: '#9ca3af', fontSize: '10px' } } },
    yAxis: { title: { text: null }, gridLineColor: '#f1f5f9', labels: { style: { color: '#9ca3af', fontSize: '10px' } } },
    legend: { enabled: true, itemStyle: { fontSize: '11px', color: '#6b7280', fontWeight: '500' } },
    tooltip: { shared: true, valueDecimals: 0 },
    plotOptions: { spline: { marker: { enabled: false, symbol: 'circle' }, lineWidth: 2 }, series: { animation: { duration: 350 } } },
    colors: PALETTE,
    series: lines.map((l) => ({ name: keyFormat(l.key) || '(not set)', data: l.values })),
  };
  if (!lines.length) return <EmptyState label="No data for this range" />;
  return (
    <Box>
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontSize="xs" color="gray.500">{sortLabel} over time</Text>
        <Text fontSize="xs" color="gray.400" border="1px solid" borderColor="gray.200" borderRadius="md" px={2} py="1px">Day</Text>
      </Flex>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}

export function ExplorerReport({
  params,
  dimension: dim0,
  dimensionOptions,      // [{value,label}] optional switcher
  columns,               // array of metric keys (table columns after the dim)
  defaultSort,           // metric key
  dimensionLabel = 'Name',
  endpoint = 'explore',  // report endpoint (explore | landing-pages | …)
  extraParams = {},      // extra query params (e.g. events= for conversions)
}) {
  const [dimension, setDimension] = useState(dim0);
  const [sort, setSort] = useState(defaultSort || columns[0]);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [plotted, setPlotted] = useState(null); // Set of keys currently plotted (null = all top)

  // debounce search → query param
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const q = useMemo(
    () => ({ ...params, ...extraParams, dimension, sort, limit: 50, ...(debounced ? { search: debounced } : {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params, dimension, sort, debounced, JSON.stringify(extraParams)]
  );
  const { data, loading, error } = useReport(endpoint, q);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const series = data?.series || { buckets: [], lines: [] };
  const sortLabel = METRIC_COLS[sort]?.label || sort;
  // Country dimension stores ISO-2 codes — show the full country name.
  const keyFormat = (val) => (dimension === 'country' ? countryName(val) : String(val ?? '(not set)'));

  const cell = (r, key) => {
    const col = METRIC_COLS[key];
    if (!col) return '—';
    const val = col.derive ? col.derive(r) : r[key];
    return col.fmt ? col.fmt(val) : val;
  };

  return (
    <ReportFrame loading={loading} error={error}>
      {/* Over-time multi-series chart */}
      <Panel>
        <MultiSeriesChart series={series} plotted={plotted} sortLabel={sortLabel} keyFormat={keyFormat} />
      </Panel>

      {/* Detail table */}
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" boxShadow="0 1px 3px rgba(16,24,40,.06)" mt={4}>
        <Flex align="center" justify="space-between" p={3} gap={3} flexWrap="wrap" borderBottom="1px solid" borderColor="gray.100">
          <HStack spacing={2}>
            {dimensionOptions && dimensionOptions.length > 1 ? (
              <Select size="sm" w="auto" value={dimension} onChange={(e) => { setDimension(e.target.value); setPlotted(null); }}>
                {dimensionOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            ) : (
              <Text fontSize="sm" fontWeight="700" color="gray.700">{dimensionLabel}</Text>
            )}
          </HStack>
          <HStack spacing={3}>
            <HStack spacing={2} flex="1" maxW="280px">
              <Icon as={FiSearch} color="gray.400" />
              <Input size="sm" variant="flushed" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </HStack>
            <Button size="xs" variant="outline" leftIcon={<FiDownload />} isDisabled={!rows.length}
              onClick={() => downloadCsv(
                `${dimension}-report`,
                [{ key: '_dim', label: dimensionLabel }, ...columns.map((k) => ({ key: k, label: METRIC_COLS[k]?.label || k }))],
                rows.map((r) => ({ ...r, _dim: keyFormat(r.key) }))
              )}>
              Export
            </Button>
          </HStack>
        </Flex>

        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th w="30px"></Th>
                <Th color="gray.500">{dimensionOptions ? (dimensionOptions.find((o) => o.value === dimension)?.label || dimensionLabel) : dimensionLabel}</Th>
                {columns.map((key) => {
                  const col = METRIC_COLS[key];
                  const active = sort === key;
                  const sortable = !col?.derive && !['returning_users', 'engagement_rate', 'views_per_user', 'events_per_session', 'avg_engagement_seconds'].includes(key);
                  return (
                    <Th
                      key={key}
                      isNumeric
                      cursor={sortable ? 'pointer' : 'default'}
                      onClick={() => sortable && setSort(key)}
                      color={active ? 'var(--brand-primary)' : 'gray.500'}
                      whiteSpace="nowrap"
                      _hover={sortable ? { color: 'var(--brand-primary)' } : undefined}
                    >
                      <HStack spacing={1} justify="flex-end">
                        {active && <Icon as={FiArrowDown} boxSize={3} />}
                        <Text>{col?.label || key}</Text>
                      </HStack>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {/* Totals row */}
              <Tr bg="gray.50" fontWeight="700">
                <Td></Td>
                <Td color="gray.700">Total</Td>
                {columns.map((key) => (
                  <Td key={key} isNumeric fontFamily="mono">
                    {cell(totals, key)}
                    <Text as="span" fontSize="10px" color="gray.400" ml={1}>100%</Text>
                  </Td>
                ))}
              </Tr>
              {rows.length === 0 && (
                <Tr><Td colSpan={columns.length + 2}><EmptyState label="No data for this range" minimal /></Td></Tr>
              )}
              {rows.map((r, i) => {
                const key = keyFormat(r.key);
                const isPlotted = series.lines.some((l) => l.key === r.key);
                const checked = isPlotted && (!plotted || plotted.has(r.key));
                return (
                  <Tr key={i} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      {isPlotted && (
                        <Checkbox
                          size="sm"
                          isChecked={checked}
                          colorScheme="blue"
                          onChange={() => {
                            setPlotted((prev) => {
                              const base = prev || new Set(series.lines.map((l) => l.key));
                              const next = new Set(base);
                              if (next.has(r.key)) next.delete(r.key); else next.add(r.key);
                              return next;
                            });
                          }}
                        />
                      )}
                    </Td>
                    <Td maxW="300px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={key} fontWeight="500">
                      {key}
                    </Td>
                    {columns.map((ckey) => {
                      const col = METRIC_COLS[ckey];
                      const share = col?.share ? r.share?.[ckey] : null;
                      return (
                        <Td key={ckey} isNumeric fontFamily="mono" whiteSpace="nowrap">
                          {cell(r, ckey)}
                          {share != null && <Text as="span" fontSize="10px" color="gray.400" ml={1}>{share}%</Text>}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        {rows.length > 0 && (
          <Text fontSize="xs" color="gray.400" p={2} textAlign="right">
            {rows.length} rows · sorted by {sortLabel}
          </Text>
        )}
      </Box>
    </ReportFrame>
  );
}

export default ExplorerReport;
